import { type ThemeOptions } from '@mui/material'
import { alpha, createTheme } from '@mui/material/styles'

import { theme as baseTheme } from '@repo/ui'
import { MuiCssBaseline } from 'settings/customCssBaseline'
import { brandRamp } from 'settings/mui/brandRamp'

/**
 * This is the OUTER, static theme — it renders before/outside the Studio theme
 * and has no tenant to read from. So its primary is Vera's own default, which
 * is BLACK, not purple (the same VERA-837 decision `settings/mui/palette.ts`
 * made for the Studio fallback).
 *
 * It was `#7F48DF` here, and this layer also hardcoded a ramp of Vera purples
 * into `palette.brand` — the two together are why an unthemed app looked like
 * it had made a brand choice nobody made.
 */
const APP_DEFAULT_PRIMARY = '#000000'

/**
 * The colour the disabled-button tints below are mixed from. Unchanged in
 * behaviour: it still resolves to `@repo/ui`'s primary, which is what these
 * rules have always rendered. Only the FALLBACK moved off Vera purple — it was
 * unreachable in practice, but an unreachable purple is still a purple waiting
 * for the day the base palette changes shape.
 */
const disabledTintBase =
  ((baseTheme.palette as any)?.primary?.main as string) || APP_DEFAULT_PRIMARY

export const appThemeOptions: ThemeOptions = {
  ...baseTheme,
  
  palette: {
    ...baseTheme.palette,
    ...(baseTheme.palette as any),
    // Derived, so this layer can never reintroduce Vera purple. Also a FULL
    // ramp — the hand-written one omitted 200/400/800/900/950 and the
    // '600-6%' focus ring, so any consumer reading those got undefined.
    brand: brandRamp(APP_DEFAULT_PRIMARY) as any,
    grey: {
      ...(baseTheme.palette as any)?.grey,
      50: '#f5f5f5',
    },
    primary: {
      ...(baseTheme.palette.primary as any),
      main: APP_DEFAULT_PRIMARY,
    },
  },
  
  shape: {
    borderRadius: 12,
  },
  
  components: {
    ...(baseTheme.components || {}),
    MuiCssBaseline: {
      styleOverrides: MuiCssBaseline,
    },
    MuiTypography: {
      defaultProps: {
        // VERA-837: `body3` is a custom @repo/ui variant, so MUI has no
        // semantic mapping for it and falls back to <span> — an INLINE box,
        // which silently ignores vertical margins (mb/mt do nothing). Every
        // body3 in the app was affected, not just one screen. Map it to <p>
        // so supporting text behaves like the block text it is.
        variantMapping: {
          body3: 'p',
        } as any,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
          textTransform: 'none',
        },
        // VERA-837: disabled must stay LEGIBLE. A blanket `opacity` fades the
        // label along with the fill, which made disabled primaries unreadable.
        // Instead: keep the button's own colours, tint only the SURFACE, and
        // leave the text at full contrast. Set at the theme so every button in
        // the app behaves the same.
        contained: {
          '&.Mui-disabled': {
            // Lighter brand fill (brand stays recognisable, not a grey slab)…
            backgroundColor: alpha(disabledTintBase, 0.55),
            // …with the label at full opacity so it stays readable.
            color: '#fff',
            boxShadow: 'none',
          },
        },
        outlined: {
          '&.Mui-disabled': {
            borderColor: alpha(disabledTintBase, 0.35),
            color: alpha(disabledTintBase, 0.65),
          },
        },
        text: {
          '&.Mui-disabled': {
            color: alpha(disabledTintBase, 0.55),
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: disabledTintBase,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
}

export const appTheme = createTheme(appThemeOptions)
